import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { publicRequest } from "../../requestMethods";
import { SellerIDContext } from "../../App";
import { isCustomDomain, getCurrentDomain } from "../../utils/domainResolver";
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  hasAnalyticsConsentAccepted,
} from "../../utils/analyticsConsent";
import {
  initGoogleAnalytics,
  trackGaPageView,
  resetGoogleAnalyticsSession,
} from "../../utils/googleAnalytics";
import {
  initMicrosoftClarity,
  resetMicrosoftClaritySession,
} from "../../utils/microsoftClarity";
import {
  setWebAnalyticsRuntime,
  resetWebAnalyticsRuntime,
} from "../../utils/webAnalyticsRuntime";
import { resetAnalyticsPurchaseDedupe } from "../../utils/webAnalyticsEvents";
import AnalyticsConsentBanner from "./AnalyticsConsentBanner";

/**
 * Loads GA4 / Clarity on custom-domain storefront only, after shopper consent.
 * No-op for subfolder URLs, missing config, or rejected consent.
 */
const WebAnalyticsBootstrap = () => {
  const sellerID = useContext(SellerIDContext);
  const location = useLocation();

  const [config, setConfig] = useState(null);
  const [consent, setConsent] = useState(null);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const isCustomDomainStore = isCustomDomain(getCurrentDomain());

  const resetSession = useCallback(() => {
    setConfig(null);
    setConsent(null);
    setAnalyticsReady(false);
    resetGoogleAnalyticsSession();
    resetMicrosoftClaritySession();
    resetWebAnalyticsRuntime();
    resetAnalyticsPurchaseDedupe();
  }, []);

  useEffect(() => {
    resetSession();

    if (!sellerID || !isCustomDomainStore) {
      return undefined;
    }

    let cancelled = false;
    setLoadingConfig(true);

    const loadConfig = async () => {
      try {
        const res = await publicRequest.get(`public/web-analytics/${sellerID}`);
        if (cancelled) return;

        const data = res.data || {};
        const gaOn = Boolean(data.googleAnalytics?.enabled && data.googleAnalytics?.measurementId);
        const clarityOn = Boolean(
          data.microsoftClarity?.enabled && data.microsoftClarity?.projectId
        );

        if (!data.hasActiveCustomDomain || (!gaOn && !clarityOn)) {
          setConfig(null);
          return;
        }

        setConfig(data);
        setConsent(getAnalyticsConsent(sellerID));
      } catch {
        if (!cancelled) setConfig(null);
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [sellerID, isCustomDomainStore, resetSession]);

  const initAnalyticsScripts = useCallback(async () => {
    if (!config) return false;

    try {
      let started = false;
      const gaId = config.googleAnalytics?.measurementId;
      const clarityId = config.microsoftClarity?.projectId;

      if (config.googleAnalytics?.enabled && gaId) {
        const ok = await initGoogleAnalytics(gaId);
        started = started || ok;
      }

      if (config.microsoftClarity?.enabled && clarityId) {
        const ok = await initMicrosoftClarity(clarityId);
        started = started || ok;
      }

      return started;
    } catch {
      return false;
    }
  }, [config]);

  useEffect(() => {
    if (!sellerID || !config || consent !== "accepted") {
      setAnalyticsReady(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      const started = await initAnalyticsScripts();
      if (!cancelled) {
        setWebAnalyticsRuntime({
          sellerID,
          gaEnabled: Boolean(config.googleAnalytics?.enabled),
          clarityEnabled: Boolean(config.microsoftClarity?.enabled),
          scriptsReady: started,
        });
        setAnalyticsReady(started);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sellerID, config, consent, initAnalyticsScripts]);

  useEffect(() => {
    if (!analyticsReady || consent !== "accepted") return;
    trackGaPageView();
  }, [analyticsReady, consent, location.pathname, location.search]);

  const handleAccept = () => {
    if (!sellerID) return;
    setAnalyticsConsent(sellerID, "accepted");
    setConsent("accepted");
  };

  const handleReject = () => {
    if (!sellerID) return;
    setAnalyticsConsent(sellerID, "rejected");
    setConsent("rejected");
    setAnalyticsReady(false);
    resetWebAnalyticsRuntime();
  };

  if (!isCustomDomainStore || !sellerID || loadingConfig || !config) {
    return null;
  }

  if (!hasAnalyticsConsentAccepted(sellerID) && consent !== "accepted") {
    if (consent === "rejected") {
      return null;
    }

    return (
      <AnalyticsConsentBanner
        consentPolicyUrl={config.consentPolicyUrl || ""}
        onAccept={handleAccept}
        onReject={handleReject}
      />
    );
  }

  return null;
};

export default WebAnalyticsBootstrap;
